<script setup lang="ts">
  import { DropdownMenu, Icon, Typo, TypoVariant } from '@travelata-libs/ui-kit';
  import type { SeoMenuItem, SeoMenuLink } from '../model/types';

  defineProps<{ items: SeoMenuItem[] }>();

  // Текущую страницу кит подсвечивает сам по `selected`.
  const getOptions = (links: SeoMenuLink[]) => links.map((link) => ({ ...link, selected: link.active === true }));

  const isActive = (item: SeoMenuItem): boolean => item.active === true || !!item.items?.some((link) => link.active);
</script>

<template>
  <nav
    class="seo-menu"
    data-testid="seo-search-menu"
  >
    <template v-for="item in items">
      <!-- persistent: ссылки групп нужны в серверном HTML (СЕО). -->
      <DropdownMenu
        v-if="item.items && item.items.length"
        :key="item.label"
        :options="getOptions(item.items)"
        trigger="hover"
        placement="bottom-start"
        persistent
        width="160px"
      >
        <template #reference>
          <span
            :class="['seo-menu__item', { 'seo-menu__item_active': isActive(item) }]"
            data-testid="seo-search-menu-group"
          >
            <Typo
              :variant="TypoVariant.BodyMRegular"
              tag="span"
            >
              {{ item.label }}
            </Typo>
            <Icon
              class="seo-menu__chevron"
              name="chevron-down"
              size="s"
            />
          </span>
        </template>
        <template #option="{ option }">
          <a
            class="seo-menu__option"
            :href="option.href"
            data-testid="seo-search-menu-option"
          >
            <Typo
              :variant="TypoVariant.BodyMRegular"
              tag="span"
            >
              {{ option.label }}
            </Typo>
          </a>
        </template>
      </DropdownMenu>
      <a
        v-else
        :key="item.label"
        :class="['seo-menu__item', { 'seo-menu__item_active': isActive(item) }]"
        :href="item.href"
        :aria-current="item.active ? 'page' : undefined"
        data-testid="seo-search-menu-link"
      >
        <Typo
          :variant="TypoVariant.BodyMRegular"
          tag="span"
        >
          {{ item.label }}
        </Typo>
      </a>
    </template>
  </nav>
</template>

<style scoped lang="scss">
  .seo-menu {
    align-items: center;
    align-self: center;
    display: flex;
    gap: var(--spacing-m);

    &__item {
      align-items: center;
      color: var(--color-text-inverse);
      cursor: pointer;
      display: flex;
      gap: var(--spacing-xxs);
      text-decoration: none;

      &_active,
      &:hover {
        text-decoration: underline;
      }
    }

    &__chevron {
      fill: var(--color-text-inverse);
    }

    // Пункт кита держит отступы на li — растягиваем ссылку на всю строку, чтобы кликалась целиком.
    &__option {
      align-items: center;
      align-self: stretch;
      color: var(--color-text-primary);
      display: flex;
      flex: 1;
      margin: 0 calc(-1 * var(--spacing-s));
      padding: 0 var(--spacing-s);
      text-decoration: none;
    }
  }
</style>
